import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

// Get Credit Note preparation data
export const getCreditNotePreparation = createAsyncThunk(
  "salesCreditNote/getCreditNotePreparation",
  async (
    { invoiceId, creditNoteType = 2, salesReturnId = null, salesCreditNoteId = null },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams();
      params.append("InvoiceId", invoiceId);
      params.append("CreditNoteType", creditNoteType);
      if (salesReturnId) params.append("SalesReturnId", salesReturnId);
      if (salesCreditNoteId) params.append("SalesCreditNoteId", salesCreditNoteId);

      const { data } = await axios.get(
        `${API_URL.SALES_CREDIT_NOTE.PREPARATION}?${params.toString()}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data);
    }
  },
);

// Get Legacy Credit Note preparation data (unrecorded invoice)
export const getLegacyCreditNotePreparation = createAsyncThunk(
  "salesCreditNote/getLegacyCreditNotePreparation",
  async (
    { salesReturnId, creditNoteType = 1, salesCreditNoteId = null },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams();
      params.append("SalesReturnId", salesReturnId);
      params.append("CreditNoteType", creditNoteType);
      if (salesCreditNoteId) params.append("SalesCreditNoteId", salesCreditNoteId);

      const { data } = await axios.get(
        `${API_URL.SALES_CREDIT_NOTE.PREPARATION_LEGACY}?${params.toString()}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data);
    }
  },
);

// Get next credit note number
export const getNextCreditNoteNumber = createAsyncThunk(
  "salesCreditNote/getNextCreditNoteNumber",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.SALES_CREDIT_NOTE.GET_NEXT_CREDIT_NOTE_NUMBER,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data);
    }
  },
);

// Create Credit Note (multipart/form-data)
export const createCreditNote = createAsyncThunk(
  "salesCreditNote/createCreditNote",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.SALES_CREDIT_NOTE.CREATE, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data);
    }
  },
);

// Update Credit Note (multipart/form-data)
export const updateCreditNote = createAsyncThunk(
  "salesCreditNote/updateCreditNote",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(API_URL.SALES_CREDIT_NOTE.UPDATE, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data);
    }
  },
);

// Update Credit Note Status
export const updateCreditNoteStatus = createAsyncThunk(
  "salesCreditNote/updateCreditNoteStatus",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        API_URL.SALES_CREDIT_NOTE.UPDATE_STATUS,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data);
    }
  },
);

// Generate IRN for a credit note (Phase 1 — generation only). Backend runs all the
// pre-flight guards (B2B, original-invoice-has-IRN, status=Issued, tenant-enabled).
export const generateCreditNoteEInvoice = createAsyncThunk(
  "salesCreditNote/generateCreditNoteEInvoice",
  async (creditNoteId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.SALES_CREDIT_NOTE.GENERATE_EINVOICE(creditNoteId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data);
    }
  },
);

// Get Credit Notes list
export const getCreditNotes = createAsyncThunk(
  "salesCreditNote/getCreditNotes",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      // Ensure payload is always a valid object
      const safePayload = payload && typeof payload === "object" ? payload : {};
      dispatch(setActivePayload(safePayload));
      const filteredPayload = Object.fromEntries(
        Object.entries(safePayload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.SALES_CREDIT_NOTE.LIST}?${queryString}`,
      );
      const credit_note_list = data.data.list || data.data || [];
      const totalRecords = data.data.totalRecords || 0;
      return { credit_note_list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data);
    }
  },
);

// Get Credit Note by ID
export const getCreditNoteById = createAsyncThunk(
  "salesCreditNote/getCreditNoteById",
  async (creditNoteId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.SALES_CREDIT_NOTE.GET_BY_ID(creditNoteId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data);
    }
  },
);

// Download Credit Note Document
export const downloadCreditNoteDocument = createAsyncThunk(
  "salesCreditNote/downloadDocument",
  async (documentId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        API_URL.SALES_CREDIT_NOTE.DOWNLOAD_DOCUMENT(documentId),
        { responseType: "blob" }
      );

      // Extract content type and filename from response headers
      const contentType = response.headers["content-type"] || "application/octet-stream";
      const contentDisposition = response.headers["content-disposition"];
      let filename = "document";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }

      // Create download link with correct MIME type
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get Credit Notes by Invoice ID
export const getCreditNotesByInvoice = createAsyncThunk(
  "salesCreditNote/getCreditNotesByInvoice",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.SALES_CREDIT_NOTE.BY_INVOICE(invoiceId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.data);
    }
  },
);

const initialState = {
  credit_note_list: [],
  credit_note_detail: null,
  credit_note_preparation: null,
  next_credit_note_number: null,
  credit_notes_by_invoice: [],
  active_payload: null,
  total_records: 0,
  loading: false,
  list_loading: false,
  detail_loading: false,
  preparation_loading: false,
};

const salesCreditNoteSlice = createSlice({
  name: "salesCreditNote",
  initialState,
  reducers: {
    setActivePayload: (state, action) => {
      state.active_payload = action.payload;
    },
    setCreditNotePreparation: (state, action) => {
      state.credit_note_preparation = action.payload;
    },
    resetCreditNotePreparation: (state) => {
      state.credit_note_preparation = null;
    },
    resetCreditNoteDetail: (state) => {
      state.credit_note_detail = null;
    },
    resetCreditNotesByInvoice: (state) => {
      state.credit_notes_by_invoice = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Credit Note Preparation
      .addCase(getCreditNotePreparation.pending, (state) => {
        state.preparation_loading = true;
      })
      .addCase(getCreditNotePreparation.fulfilled, (state, action) => {
        state.preparation_loading = false;
        state.credit_note_preparation = action.payload;
      })
      .addCase(getCreditNotePreparation.rejected, (state) => {
        state.preparation_loading = false;
      })
      // Get Legacy Credit Note Preparation
      .addCase(getLegacyCreditNotePreparation.pending, (state) => {
        state.preparation_loading = true;
      })
      .addCase(getLegacyCreditNotePreparation.fulfilled, (state, action) => {
        state.preparation_loading = false;
        state.credit_note_preparation = action.payload;
      })
      .addCase(getLegacyCreditNotePreparation.rejected, (state) => {
        state.preparation_loading = false;
      })
      // Get Next Credit Note Number
      .addCase(getNextCreditNoteNumber.pending, (state) => {
        state.loading = true;
      })
      .addCase(getNextCreditNoteNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.next_credit_note_number = action.payload;
      })
      .addCase(getNextCreditNoteNumber.rejected, (state) => {
        state.loading = false;
      })
      // Create Credit Note
      .addCase(createCreditNote.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCreditNote.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createCreditNote.rejected, (state) => {
        state.loading = false;
      })
      // Update Credit Note
      .addCase(updateCreditNote.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCreditNote.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateCreditNote.rejected, (state) => {
        state.loading = false;
      })
      // Update Credit Note Status
      .addCase(updateCreditNoteStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCreditNoteStatus.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateCreditNoteStatus.rejected, (state) => {
        state.loading = false;
      })
      // Get Credit Notes List
      .addCase(getCreditNotes.pending, (state) => {
        state.list_loading = true;
      })
      .addCase(getCreditNotes.fulfilled, (state, action) => {
        state.list_loading = false;
        state.credit_note_list = action.payload.credit_note_list;
        state.total_records = action.payload.data.totalRecords || 0;
      })
      .addCase(getCreditNotes.rejected, (state) => {
        state.list_loading = false;
      })
      // Get Credit Note by ID
      .addCase(getCreditNoteById.pending, (state) => {
        state.detail_loading = true;
      })
      .addCase(getCreditNoteById.fulfilled, (state, action) => {
        state.detail_loading = false;
        state.credit_note_detail = action.payload;
      })
      .addCase(getCreditNoteById.rejected, (state) => {
        state.detail_loading = false;
      })
      // Get Credit Notes by Invoice
      .addCase(getCreditNotesByInvoice.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCreditNotesByInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.credit_notes_by_invoice = action.payload;
      })
      .addCase(getCreditNotesByInvoice.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  setActivePayload,
  setCreditNotePreparation,
  resetCreditNotePreparation,
  resetCreditNoteDetail,
  resetCreditNotesByInvoice,
} = salesCreditNoteSlice.actions;

export default salesCreditNoteSlice.reducer;